import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { LstUserService } from '@savvaslearning/lst-auth';
import { firstValueFrom } from 'rxjs';

import { BackendRoutingService } from '../backend-routing/backend-routing.service';
import { AuthStore } from '../../stores/auth.store';
import { SessionStorageService } from '../../storage/session-storage.service';
import type { ProgramResolution } from './program-resolution.types';

/**
 * Resolves a Realize product-grade via the new backend. New-flow replacement for
 * the legacy token -> GradeInfo -> productGradeId step.
 */
@Injectable({ providedIn: 'root' })
export class ProgramResolutionService {
  private readonly http = inject(HttpClient);
  private readonly sessionStorage = inject(SessionStorageService);
  private readonly auth = inject(AuthStore);
  private readonly lstUserService = inject(LstUserService);
  private readonly routing = inject(BackendRoutingService);

  private get base(): string {
    return `${this.routing.baseUrl}/v1/product-grades`;
  }

  /**
   * Resolves a (productCode, gradeCode) pair to its product-grade via the new backend
   * (GET /v1/product-grades?productCode=...&gradeCode=...). New-flow entry
   * when Realize launches by product + grade rather than programId + version.
   */
  async resolveByProductGrade(
    productCode: string,
    gradeCode: string,
  ): Promise<ProgramResolution> {
    const url =
      `${this.base}?productCode=${encodeURIComponent(productCode)}` +
      `&gradeCode=${encodeURIComponent(gradeCode)}`;
    return firstValueFrom(
      this.http.get<ProgramResolution>(url, { headers: this.authHeaders() }),
    );
  }

  /**
   * New-flow auth: the RBS bearer from LstAuth (primary, set after SSO). Dev override
   * via sessionStorage (full Authorization value, or a bearer token) so
   * curl-parity / pre-SSO testing works.
   */
  private authHeaders(): HttpHeaders {
    let headers = new HttpHeaders({ 'Content-Type': 'application/json; charset=utf-8' });
    const rawAuth = this.sessionStorage.get('classviewServiceAuth');
    if (rawAuth) {
      headers = headers.set('Authorization', rawAuth);
    } else {
      const token =
        (this.sessionStorage.get('classviewServiceToken') || null) ??
        this.lstUserService.getRbsToken();
      if (token) headers = headers.set('Authorization', `Bearer ${token}`);
    }
    const userId = this.auth.userId();
    if (userId) headers = headers.set('userId', userId);
    return headers;
  }
}
